import { AuthService } from './authService';
import { ItemBinSizeDto, CreateItemBinSizeRequest, UpdateItemBinSizeRequest, ItemBinSizePagedQueryParams, ItemBinSizePagedResult } from '../types/itemBinSize';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class ItemBinSizeService {
  private baseUrl = `${API_BASE_URL}/item-bin-size`;

  async getPaged(params: ItemBinSizePagedQueryParams): Promise<ItemBinSizePagedResult> {
    const queryParams = new URLSearchParams();
    if (params.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.sortColumn) queryParams.append('sortColumn', params.sortColumn);
    if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);
    if (params.itemNo) queryParams.append('itemNo', params.itemNo);
    if (params.itemDescription) queryParams.append('itemDescription', params.itemDescription);
    if (params.binSizeNo) queryParams.append('binSizeNo', params.binSizeNo);
    if (params.binSizeDescription) queryParams.append('binSizeDescription', params.binSizeDescription);
    if (params.maxQuantity) queryParams.append('maxQuantity', params.maxQuantity);
    if (params.putAwayPriorityNo) queryParams.append('putAwayPriorityNo', params.putAwayPriorityNo);
    if (params.groupDescription) queryParams.append('groupDescription', params.groupDescription);
    if (params.type) queryParams.append('type', params.type);
    if (params.binSizeNos) queryParams.append('binSizeNos', params.binSizeNos);
    if (params.groupIDs) queryParams.append('groupIDs', params.groupIDs);

    const response = await AuthService.authenticatedGet(`${this.baseUrl}/paged?${queryParams.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getByCompanyItemAndBinSize(companyNo: string, itemNo: string, binSizeNo: number): Promise<ItemBinSizeDto | null> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${encodeURIComponent(itemNo)}/${binSizeNo}`);
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error: any) {
      if (error.message?.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async getByCompanyNo(companyNo: string): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getByCompanyAndItem(companyNo: string, itemNo: string): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}/item/${encodeURIComponent(itemNo)}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getCurrent(): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async getAll(): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  }

  async create(request: CreateItemBinSizeRequest): Promise<ItemBinSizeDto> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  async update(id: number, request: UpdateItemBinSizeRequest): Promise<ItemBinSizeDto> {
    const response = await AuthService.authenticatedPut(`${this.baseUrl}/${id}`, request);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }

  async delete(id: number): Promise<void> {
    const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${id}`);
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
  }

  async bulkUpdateForItem(companyNo: string, itemNo: string, itemBinSizes: ItemBinSizeDto[]): Promise<ItemBinSizeDto[]> {
    const response = await AuthService.authenticatedPut(
      `${this.baseUrl}/bulk/company/${companyNo}/item/${encodeURIComponent(itemNo)}`,
      itemBinSizes
    );
    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(extractApiError(error, response, 'HTTP error!'));
    }
    return await response.json();
  }
}

export const itemBinSizeService = new ItemBinSizeService();
